import { Fragment, useRef, useState, useEffect, useCallback } from "react";
import { useMatch, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Button, Container, Form } from "react-bootstrap";
import { Logout, RegisterToken, RequestToken, RequestTokenWithSession } from "../../graphQL/api";
import { DmwaError } from "../../models/DmwaError";
import ToastView from "../common/ToastView";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IsLoggedIn, clearToken, setAppId, setToken, getAppId, generateAppId } from "../../redux/tokenSlice";
import { Paths } from "../routes/Routes";
import ModalView from "../common/ModalView";
import { ApolloConsumer, useApolloClient } from "@apollo/client";
import { clearAppState } from "../../redux/appStateSlice";
import { clearSelectedGroup } from "../../redux/navigationSlice";
import { persistor } from "../../redux/store";
import GridlineLogo from '../../assets/img/Gridline-DMWA-Logo.png';
import GridlineLogoLight from '../../assets/img/Gridline-DMWA-Logo-Light.png';
import { isIos, renderBannerInstall, renderIOSBannerInstall } from "../../helpers/renderHelper";
import ReactGA from "react-ga4";
import { AppTheme, getAppliedTheme, toggleTheme, THEME_DARK } from "../../helpers/themeHelper";

const Login = () => {

	const loginMatch = useMatch(`${Paths.LOGIN}/:customProtocol`);

  	const [searchParams] = useSearchParams();
	const [validated] = useState(false);
	const [showLoading, setShowLoading] = useState(false);
	const [showLogout, setShowLogout] = useState(false);
	const [error, setError] = useState<DmwaError| undefined>();
	const [showPrompt, setShowPrompt ] = useState(false)
	const [currentTheme, setCurrentTheme] = useState<AppTheme>(getAppliedTheme())

	var geoTabDBName = searchParams.get('geoTabDBName');
	var userName = searchParams.get('userName');
	var authToken = searchParams.get('authToken');

	const customProtocolUrl = loginMatch?.params.customProtocol
	if (customProtocolUrl) {
		const newUrl = customProtocolUrl.replace("web+gridlinedriverlogin://","https://mobiledev.grindline.com/?")
		
		const customSearchParams = new URLSearchParams(new URL(newUrl).search);
		geoTabDBName = customSearchParams.get('geoTabDBName');
		userName = customSearchParams.get('userName');
		authToken = customSearchParams.get('authToken');
	}

	const isGeoTabAuthRequest = (geoTabDBName !== null) && (userName !== null) && (authToken !== null);

	const apolloClient = useRef<any>()
	const apolloClientHook = useApolloClient()

	const dispatch = useAppDispatch();
	const isLoggedIn = IsLoggedIn();
	const navigate = useNavigate()

	const bondedPairToken = useAppSelector(state => state.token.bondedPairToken)

	const deferredPrompt = useRef<any>(null);
	useEffect(() => {
		/**
		 * Captures the browser install prompt so the login install banner can show it.
		 */
		function handleBeforeInstallPrompt(event: Event) {
			event.preventDefault();
			deferredPrompt.current = event;
			setShowPrompt(true);
		}

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		};
	}, []);

	/**
	 * Switches the login page between light and dark themes.
	 */
	function handleThemeToggle() {
		const nextTheme = toggleTheme();
		setCurrentTheme(nextTheme);
	}

	const themeToggleButton = (
		<button
			type="button"
			className="app-login-theme-toggle"
			onClick={handleThemeToggle}
			aria-label={currentTheme === THEME_DARK ? "Switch to light theme" : "Switch to dark theme"}
			title={currentTheme === THEME_DARK ? "Switch to light theme" : "Switch to dark theme"}
		>
			<i className={`fa-solid ${currentTheme === THEME_DARK ? "fa-sun" : "fa-moon"}`} />
		</button>
	);

	const registerToken = useCallback(async (token:string) => {
		setShowLoading(true);
		RegisterToken(token, async response => {
			setShowLoading(false);
			if (response instanceof DmwaError) {
				if (response.status === 403) {
					response.message = "Could not register token";
				}
				setError(response);
			} else {
				const bondedPair = response?.data.tokens;
				if(bondedPair) {
					try {
						// 1. Clear Apollo cache first to prevent serving stale data from previous user
						await apolloClientHook.resetStore();

						// 2. Clear Redux state to remove old user data
						dispatch(clearAppState());
						dispatch(clearSelectedGroup());

						// 3. Set new token after cache and state are cleared
						dispatch(setToken(bondedPair));

						var appId = getAppId();

						if (appId === undefined) {
							appId = generateAppId();
							dispatch(setAppId(appId as string));
						}

						// 4. Navigate after cache is cleared and token is set
						navigate(Paths.HOME);
					} catch (error) {
						console.error('Error during token registration:', error);
						setError(new DmwaError(500, "Failed to clear cache during login"));
					}
				} else {
					//We did not get a valid token, throw an error
					setError(new DmwaError(403, "Register token is missing"));
				}
			}
		})
	}, [dispatch, navigate, apolloClientHook])

	const loginWithGeoTab = useCallback((geoTabDBName?: string | null, userName?: string | null, authToken?: string | null) => {
		setShowLoading(true);

		if (geoTabDBName && userName && authToken) {
			RequestTokenWithSession(geoTabDBName, userName, authToken, response => {
				//console.log('response = ', response);
				setShowLoading(false);
				if (response instanceof DmwaError) {
					window.location.replace(`${import.meta.env.REACT_APP_AUTH_ERROR_URL}`)
				} else {
					const token = response?.data.token;
					if(token) {
						registerToken(token);
					} else {
						//We did not get a valid token, throw an error
						setError(new DmwaError(403, "Login token is missing"))
					}
				}
			})
		}
	}, [registerToken])

	useEffect(() => {
		if (isGeoTabAuthRequest) {
			const performLogoutAndLogin = async () => {
				if (isLoggedIn && bondedPairToken) {
					try {
						// 1. Clear Apollo cache first to prevent queries from using stale data
						await apolloClientHook.resetStore();

						// 2. Clear Redux state
						dispatch(clearToken());
						dispatch(clearAppState());
						dispatch(clearSelectedGroup());

						// 3. Clear Google Analytics properties
						if (import.meta.env.REACT_APP_GA_ID) {
							ReactGA.gtag('set', 'user_properties', {
								var2: null,
								var1: null
							});
							
							ReactGA.gtag('config', import.meta.env.REACT_APP_GA_ID, {
								var2: null,
								var3: null,
								var1: null
							});
							
							console.log('GA user properties and config cleared on account switch');
						}

						// 4. Purge persisted storage
						await persistor.purge();

						// 5. Call logout API, then login with new credentials
						Logout(bondedPairToken, getAppId(), response => {
							loginWithGeoTab(geoTabDBName, userName, authToken);
						});
					} catch (error) {
						console.error('Error during account switch:', error);
						// Still attempt login even if cleanup fails
						loginWithGeoTab(geoTabDBName, userName, authToken);
					}
				} else {
					loginWithGeoTab(geoTabDBName, userName, authToken);
				}
			};

			performLogoutAndLogin();
		}
	}, [authToken, bondedPairToken, dispatch, geoTabDBName, isGeoTabAuthRequest, isLoggedIn, loginWithGeoTab, userName, apolloClientHook])

	function submitClicked(e) {
		e.preventDefault();
		e.stopPropagation();

		const form = e.currentTarget;

		setShowLoading(true);
		RequestToken(form.floatingClientId.value, form.floatingUserName.value, form.floatingPassword.value, response => {
			//console.log('response = ', response);
			setShowLoading(false);
			if (response instanceof DmwaError) {

    			window.location.replace(`${import.meta.env.REACT_APP_AUTH_ERROR_URL}`)
				// if (response.status === 403) {
				// 	response.message = "Invalid Client Id, User name or Password";
				// }
				// setError(response);
			} else {
				const token = response?.data.token;
				if(token) {
					registerToken(token);
				} else {
					//We did not get a valid token, throw an error
					setError(new DmwaError(403, "Login token is missing"))
				}
			}
		})
	}

	function toggleToast() {
		setError(undefined);
	}

	function logOut(e, client) {
		e.preventDefault();

		apolloClient.current = client

		setShowLogout(true);
	}

	async function signOutClicked() {
		setShowLogout(false);

		try {
			// 1. Clear Apollo cache first to prevent queries from using stale data
			if (apolloClient.current) {
				await apolloClient.current.resetStore();
			}

			// 2. Clear Redux state
			dispatch(clearToken());
			dispatch(clearAppState());
			dispatch(clearSelectedGroup());

			// 3. Clear Google Analytics properties
			if (import.meta.env.REACT_APP_GA_ID) {
				ReactGA.gtag('set', 'user_properties', {
					var2: null,
					var1: null
				});
				
				ReactGA.gtag('config', import.meta.env.REACT_APP_GA_ID, {
					var2: null,
					var3: null,
					var1: null
				});
				
				console.log('GA user properties and config cleared on logout');
			}

			// 4. Purge persisted storage
			await persistor.purge();

			// 5. Call logout API (fire and forget)
			if (bondedPairToken) {
				Logout(bondedPairToken, getAppId(), response => {});
			}

			// 6. Navigate after all cleanup is complete
			navigate(Paths.LOGIN);
		} catch (error) {
			console.error('Error during sign out:', error);
			// Still navigate even if cleanup fails to prevent user from being stuck
			navigate(Paths.LOGIN);
		}
	}


	if (isLoggedIn && !isGeoTabAuthRequest) {
		return (
			<Fragment>
				{themeToggleButton}
				<ModalView 
					title="" 
					message="Are you sure you want to sign out?" 
					onClose={()=>setShowLogout(false)} 
					shouldShow={showLogout} 
					confirmTitle="Sign Out"
					confirmVariant="danger"
					onConfirm={()=>signOutClicked()}/>

				<Container style={{marginTop:40}}>
					<ApolloConsumer>
						{  client => 
							<Fragment>
								<p>You are already signed in.</p>
								<Button variant="warning" onClick={ e => logOut(e, client) } >Logout</Button>
							</Fragment>
						}
						
					</ApolloConsumer>
				</Container>
			</Fragment>
		)
	}

	return (
		<Fragment>
			{themeToggleButton}
			<ToastView title="Error" message={error && error.message} shouldShow={error !== undefined} showToggle={toggleToast} />
			{ showLoading && <LoadingSpinner />}
			<Container style={{marginTop:40}}>
				<div className="row align-items-center row-cols-1">
					<div className="col col-12">
					<div className="card" style={{marginBottom: "0.35rem !important"}}>
						<div className="card-body" style={{padding: "0.35rem !important"}}>
						<div><img className="mb-0 img-fluid" src={currentTheme === THEME_DARK ? GridlineLogo : GridlineLogoLight} width="100%" height="auto" alt="Driver Score powered by GRIDLINE" style={{padding:"0 0"}} /></div>
						</div>
					</div>
					</div>
				</div>
				
				{ showPrompt && !isIos() &&
					<div className="row align-items-center row-cols-1" >
						{ renderBannerInstall(deferredPrompt, () => {
							setShowPrompt(false);
						}) }
					</div>
				}
				
				<div className="row align-items-center row-cols-1" >
					<div className="col col-12 align-items-center">
					<div className="card">
						<div className="card-body" style={{padding: "0.35rem !important"}}>
						<div style={{width:"100% !important", padding:"0 0 !important"}}>
							<Form className="app-login-form" noValidate validated={validated} onSubmit={submitClicked}>
								<div className="form-floating">
									<input type="id" className="form-control" id="floatingClientId" placeholder="Client ID" />
									<label htmlFor="floatingClientId">Client ID</label>
								</div>
								<div className="form-floating">
									<input type="username" className="form-control" id="floatingUserName" placeholder="Username" />
									<label htmlFor="floatingUserName">Username</label>
								</div>
								<div className="form-floating">
									<input type="password" className="form-control" id="floatingPassword" placeholder="Password" />
									<label htmlFor="floatingPassword">Password</label>
								</div>
								<button className="w-100 btn btn-lg btn-primary app-login-submit-button" type="submit">Log in </button>
							</Form> 
						</div>
						</div>
					</div>
					</div>
				</div>
				
				<div className="row align-items-center row-cols-1" >
					{ renderIOSBannerInstall() }
				</div>
				

			</Container>
		</Fragment>

	)
}

export default Login;